import { Box, Stack, Tooltip, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import type { Sums } from 'src/context/api/campaignLeaks/api';
import Iconify from 'src/components/iconify';
import BarCell from '../shared/bar-cell';
import { anyOfOperators } from '../shared/grid-operators';
import type { CampaignLeaksT } from '../shared/i18n';
import { MetricDef, PerformanceRow, brandLabel, evolution, formatMetric, formatShare, share, totalRowShare } from './utils';

const NUM_SX = { fontFeatureSettings: '"lnum" 1, "tnum" 1' };

export const TOTAL_ID = '__total__';

type BuildArgs = {
  ct: CampaignLeaksT;
  metrics: MetricDef[];
  rows: PerformanceRow[];
  total: { current: Sums; compare: Sums | null };
  currency: string;
  hasCompare: boolean;
  percent: boolean;
  partialCoverage: boolean;
  campaignTypes: string[];
};

function EvolutionLabel({ label, color }: { label: string; color: string }) {
  return (
    <Typography
      data-testid="evolution"
      sx={{ fontSize: 12, lineHeight: 1.2, color, textAlign: 'right', minWidth: 46, whiteSpace: 'nowrap', ...NUM_SX }}
    >
      {label}
    </Typography>
  );
}

const header = (label: string, partialCoverage: string | null) => () => (
  <Stack direction="row" alignItems="center" spacing={0.5}>
    <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#637381' }}>{label}</Typography>
    {partialCoverage && (
      <Tooltip title={partialCoverage} placement="top">
        <Box component="span" sx={{ display: 'inline-flex' }} data-testid="coverage-warning">
          <Iconify icon="solar:danger-triangle-linear" width={14} sx={{ color: '#FFAB00' }} />
        </Box>
      </Tooltip>
    )}
  </Stack>
);

export const buildPerformanceColumns = ({
  ct,
  metrics,
  rows,
  total,
  currency,
  hasCompare,
  percent,
  partialCoverage,
  campaignTypes,
}: BuildArgs): GridColDef<PerformanceRow>[] => {
  const isTotal = (row: PerformanceRow) => row.id === TOTAL_ID;
  const brandOptions = [brandLabel(ct, true), brandLabel(ct, false)];
  const coverageWarning = hasCompare && partialCoverage ? ct('columns.partial_coverage') : null;
  const metricColumn = (metric: MetricDef): GridColDef<PerformanceRow> => {
    const max = Math.max(0, ...rows.map((row) => metric.value(row.current) ?? 0));
    const totalValue = metric.value(total.current) ?? 0;
    const totalShare = totalRowShare(metric, rows, total.current);
    return {
      field: metric.key,
      headerName: ct(metric.labelKey),
      type: 'number',
      headerAlign: 'left',
      minWidth: metric.additive ? 300 : 140,
      flex: metric.additive ? 2 : 1,
      filterable: false,
      renderHeader: header(ct(metric.labelKey), coverageWarning),
      renderCell: ({ row }) => {
        const value = metric.value(row.current);
        const label = formatMetric(metric.kind, value, currency);
        const evo = hasCompare ? <EvolutionLabel {...evolution(metric, row.current, row.compare, { percent, currency })} /> : undefined;
        if (metric.additive) {
          return (
            <BarCell
              label={label}
              value={value ?? 0}
              max={max}
              showBar={!isTotal(row)}
              share={formatShare(isTotal(row) ? totalShare : share(value ?? 0, totalValue))}
              evolution={evo}
            />
          );
        }
        return (
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: 1 }}>
            <Typography sx={{ fontSize: 14, color: '#2F3843', whiteSpace: 'nowrap', ...NUM_SX }}>{label}</Typography>
            {evo}
          </Stack>
        );
      },
    };
  };

  return [
    {
      field: 'campaign_type',
      headerName: ct('columns.type'),
      // Wide enough for the longest campaign types ("Performance Max", "Demand Gen") on one line.
      minWidth: 170,
      type: 'singleSelect',
      valueOptions: campaignTypes,
      filterOperators: anyOfOperators(ct, campaignTypes, ct('filters.select_types')),
      renderCell: ({ row }) =>
        isTotal(row) ? (
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#919EAB' }}>{ct('columns.total')}</Typography>
        ) : (
          <Typography sx={{ fontSize: 14, color: '#2F3843' }}>{row.campaign_type}</Typography>
        ),
    },
    {
      field: 'is_brand',
      headerName: ct('columns.brand_non_brand'),
      minWidth: 160,
      type: 'singleSelect',
      valueOptions: brandOptions,
      valueGetter: (_value, row) => brandLabel(ct, row.is_brand),
      filterOperators: anyOfOperators(ct, brandOptions),
      renderCell: ({ row }) =>
        isTotal(row) ? null : (
          <Box
            sx={{
              px: '10px',
              py: '4px',
              borderRadius: '28px',
              bgcolor: row.is_brand ? 'rgba(183, 220, 251, 0.2)' : 'rgba(36, 165, 108, 0.08)',
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.3, color: '#333C47' }}>{brandLabel(ct, row.is_brand)}</Typography>
          </Box>
        ),
    },
    ...metrics.map(metricColumn),
  ];
};
